import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X, ArrowRight } from 'lucide-react'
import { products, lineLabels } from '@/data/products'
import { LocalizedLink } from '@/components/LocalizedLink'
import { type RouteKey } from '@/i18n/routes'

interface SearchOverlayProps {
  isOpen: boolean
  onClose: () => void
}

const searchablePages: { routeKey: RouteKey; labelKey: string; defaultLabel: string }[] = [
  { routeKey: 'home', labelKey: 'nav.home', defaultLabel: 'Home' },
  { routeKey: 'products', labelKey: 'nav.products', defaultLabel: 'Prodotti' },
  { routeKey: 'productsNovita', labelKey: 'nav.productsNovita', defaultLabel: 'Novità' },
  { routeKey: 'productsHair', labelKey: 'nav.productsCapelli', defaultLabel: 'Capelli' },
  { routeKey: 'productsCorpo', labelKey: 'nav.productsCorpo', defaultLabel: 'Corpo' },
  { routeKey: 'productsViso', labelKey: 'nav.productsViso', defaultLabel: 'Viso' },
  { routeKey: 'productsFragranze', labelKey: 'nav.productsFragranze', defaultLabel: 'Fragranze' },
  { routeKey: 'productsUomo', labelKey: 'nav.productsUomo', defaultLabel: 'Uomo' },
  { routeKey: 'world', labelKey: 'nav.world', defaultLabel: 'Il Mondo Nashi' },
  { routeKey: 'welcomeBox', labelKey: 'nav.welcomeBox', defaultLabel: 'Welcome Box' },
  { routeKey: 'news', labelKey: 'nav.news', defaultLabel: 'News' },
  { routeKey: 'contacts', labelKey: 'nav.contacts', defaultLabel: 'Contattaci' },
  { routeKey: 'faq', labelKey: 'nav.faq', defaultLabel: 'FAQ' },
  { routeKey: 'storeLocator', labelKey: 'utility.storeLocator', defaultLabel: 'Store Locator' },
  { routeKey: 'salon', labelKey: 'nav.salon', defaultLabel: 'Store & Salon' },
  { routeKey: 'nashiStore', labelKey: 'nav.nashiStore', defaultLabel: 'Nashi Store' },
  { routeKey: 'nashiPartner', labelKey: 'nav.nashiPartner', defaultLabel: 'Nashi Partner' },
  { routeKey: 'nashiLoversClub', labelKey: 'nav.nashiLoversClub', defaultLabel: 'Nashi Lovers Club' },
  { routeKey: 'diventaPartner', labelKey: 'nav.diventaPartner', defaultLabel: 'Diventa Partner' },
  { routeKey: 'eGiftCard', labelKey: 'nav.eGiftCard', defaultLabel: 'E-Gift Card' },
  { routeKey: 'lavoraConNoi', labelKey: 'nav.lavoraConNoi', defaultLabel: 'Lavora con noi' },
  { routeKey: 'regaliAziendali', labelKey: 'nav.regaliAziendali', defaultLabel: 'Regali Aziendali' },
  { routeKey: 'privacy', labelKey: 'footer.privacy', defaultLabel: 'Privacy' },
  { routeKey: 'cookiePolicy', labelKey: 'footer.cookiePolicy', defaultLabel: 'Cookie Policy' },
]

export function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const { t, i18n } = useTranslation()
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const lang = i18n.language as 'it' | 'en'

  useEffect(() => {
    if (isOpen) {
      setQuery('')
      const id = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(id)
    }
  }, [isOpen])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handleEsc)
      return () => window.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  const q = query.trim().toLowerCase()

  const productResults = useMemo(() => {
    if (!q) return []
    return products
      .filter((p) => {
        const nameMatch = p.name.toLowerCase().includes(q)
        const lineMatch = lineLabels[p.line]?.toLowerCase().includes(q)
        const catMatch = p.category.toLowerCase().includes(q)
        return nameMatch || lineMatch || catMatch
      })
      .slice(0, 8)
  }, [q])

  const pageResults = useMemo(() => {
    if (!q) return []
    return searchablePages
      .filter((p) => {
        const label = t(p.labelKey, p.defaultLabel).toLowerCase()
        return label.includes(q)
      })
      .slice(0, 8)
  }, [q, t])

  const hasResults = productResults.length > 0 || pageResults.length > 0

  const productBasePath = lang === 'it' ? 'prodotti' : 'products'

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="absolute left-0 right-0 top-full bg-cream border-b border-bruno/10 shadow-[0_8px_30px_-12px_rgba(59,49,46,0.18)] z-[1200]"
        >
          <div className="nashi-container py-6 md:py-8">
            <div className="relative mx-auto max-w-2xl">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-bruno-soft pointer-events-none"
                strokeWidth={1.5}
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('search.placeholder', 'Cerca prodotti o pagine...')}
                className="w-full bg-transparent border-b-2 border-bruno/20 py-3 pl-12 pr-12 text-lg text-bruno placeholder:text-bruno-soft/60 focus:border-argan-deep focus:outline-none transition-colors"
              />
              <button
                onClick={onClose}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-bruno-soft hover:text-bruno transition-colors"
                aria-label={t('aria.close', 'Chiudi')}
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            {q && (
              <div className="mx-auto max-w-2xl mt-6 max-h-[60vh] overflow-y-auto">
                {!hasResults ? (
                  <p className="text-center text-bruno-soft py-6">
                    {t('search.noResults', 'Nessun risultato trovato')}
                  </p>
                ) : (
                  <div className="space-y-6">
                    {productResults.length > 0 && (
                      <div>
                        <h3 className="text-[11px] uppercase tracking-[0.18em] text-bruno-soft mb-3">
                          {t('nav.products', 'Prodotti')}
                        </h3>
                        <ul className="space-y-1">
                          {productResults.map((product) => (
                            <li key={product.slug}>
                              <a
                                href={`/${lang}/${productBasePath}/${product.slug}`}
                                onClick={onClose}
                                className="group flex items-center justify-between py-2 border-b border-bruno/10 text-bruno hover:text-argan-deep transition-colors"
                              >
                                <div>
                                  <span className="font-medium">{product.name}</span>
                                  <span className="ml-2 text-sm text-bruno-soft">
                                    {lineLabels[product.line]}
                                  </span>
                                </div>
                                <ArrowRight
                                  className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                  strokeWidth={1.5}
                                />
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {pageResults.length > 0 && (
                      <div>
                        <h3 className="text-[11px] uppercase tracking-[0.18em] text-bruno-soft mb-3">
                          {t('search.pages', 'Pagine')}
                        </h3>
                        <ul className="space-y-1">
                          {pageResults.map((page) => (
                            <li key={page.routeKey}>
                              <LocalizedLink
                                to={page.routeKey}
                                onClick={onClose}
                                className="group flex items-center justify-between py-2 border-b border-bruno/10 text-bruno hover:text-argan-deep transition-colors"
                              >
                                <span className="font-medium">
                                  {t(page.labelKey, page.defaultLabel)}
                                </span>
                                <ArrowRight
                                  className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                  strokeWidth={1.5}
                                />
                              </LocalizedLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
